import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import Modal from './ui/Modal'
import createMarkup from '~/utils/createMarkup'
import CheckboxWithLabel from './ui/CheckboxWithLabel'
import Button from './ui/Button'
import moment from 'moment'
import { ArrowUturnLeftIcon, ArrowUturnRightIcon, CheckCircleIcon } from '@heroicons/react/24/solid'
import classNames from '~/utils/classNames'
import SignatureCanvas, { ReactSignatureCanvasProps } from 'react-signature-canvas'
import { useReactToPrint } from 'react-to-print'
import { TrashIcon } from '@heroicons/react/24/outline'
import parseDateTime from '~/utils/parseDateTime'
import _ from 'lodash'

interface ComponentProps {
  open: boolean
  onClose: () => void
  campName: string
  rules: string
  legalAcknowledgements: string[]
  isTermsAgreed: boolean
  setIsTermsAgreed: Dispatch<SetStateAction<boolean>>
  tos_signature?: string
  tos_agreed_at?: string
  handleAcceptAndSign: (tos_signature?: string) => void
  isAdminView?: boolean
}

const TermsAgreementModal = ({
  open,
  onClose,
  campName,
  rules,
  legalAcknowledgements,
  isTermsAgreed,
  tos_signature,
  tos_agreed_at,
  handleAcceptAndSign,
  isAdminView,
}: ComponentProps) => {
  
  const [ref, setRef] = useState<ReactSignatureCanvasProps>()

  const [states, setStates] = useState<any[]>([])
  const [currentIndex, setCurrentIndex] = useState(-1)
  const [isRerendered, setIsRerendered] = useState(false)
  const termsPrintref = useRef(null)

  const saveState = () => {
    const newState = _.cloneDeep(ref?.toData())
    setStates([..._.cloneDeep(states), newState])
    setCurrentIndex((prev) => prev + 1)
  }

  const undo = () => {
    if (currentIndex >= 0) {
      setCurrentIndex((prev) => prev - 1)
      ref?.fromData(states[currentIndex - 1])
    }
  }

  const redo = () => {
    if (currentIndex < states.length - 1) {
      setCurrentIndex((prev) => prev + 1)
      ref?.fromData(states[currentIndex + 1])
    }
  }

  const clearCanvas = () => {
    const newState = []
    ref?.fromData(newState)
    setStates([..._.cloneDeep(states), _.cloneDeep(newState)])
    setCurrentIndex((prev) => prev + 1)
  }

  const handleDrawEnd = () => {
    saveState()
  }

  const undoDisabled = currentIndex < 0
  const redoDisabled = currentIndex >= states.length - 1
  const isCanvasEmpty = ref?.isEmpty()

  const rerenderCanvas = () => {
    setIsRerendered(false)
    setIsRerendered(true)
  }

  useEffect(() => {
    rerenderCanvas()
  }, [])

  useEffect(() => {
    if (isTermsAgreed) {
      ref?.fromDataURL(tos_signature)
      rerenderCanvas()
    }
  }, [isTermsAgreed])

  const usePrint = useReactToPrint({
    content: () => termsPrintref.current,
  })

  const handlePrint = async () => {
    if (termsPrintref.current) {
      termsPrintref.current.style.display = 'block'
      usePrint()
      termsPrintref.current.style.display = 'none'
    }
  }

  useEffect(() => {
    
    const canvas = document.querySelector('.sigCanvas')
    const parent_canvas = document.getElementById('parentCanvas')

    if (canvas) {
      canvas.setAttribute('width', parent_canvas?.offsetWidth)
      canvas.setAttribute('height', parent_canvas?.offsetHeight)
    }
  }, [ref])

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-7xl">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleAcceptAndSign(ref?.getTrimmedCanvas().toDataURL())
        }}
      >
        <div className="text-2xl font-semibold mb-8">{campName} Terms & Conditions</div>
        {rules?.length && (
          <>
            <div ref={termsPrintref} className={classNames("py-8 xsm:px-4 hidden")}>
              <div
                className={`${"rules-container html-editor-container"}`}
                dangerouslySetInnerHTML={createMarkup(rules)}
              />
              {tos_signature && (
                <div className="mt-10 max-w-96">
                  <img src={tos_signature} alt="sign" />
                </div>
              )}
            </div>
            <div
              className={classNames("bg-gray-150 rounded-md mb-8 py-8 px-2 xsm:px-4 overflow-y-scroll max-h-[50vh]")}
            >
              <div
                className={`${"rules-container html-editor-container"}`}
                dangerouslySetInnerHTML={createMarkup(rules)}
              />
            </div>
          </>
        )}

        <div className="mb-6">
          {legalAcknowledgements &&
            !isTermsAgreed &&
            legalAcknowledgements.map((acknowledgement, id) => {
              return (
                <CheckboxWithLabel
                  name={id.toString()}
                  labelHtml={createMarkup(acknowledgement)}
                  customColor="#E13233"
                  required
                />
              )
            })}
        </div>
        {!tos_signature?.length ? (
          <>
            {!(!tos_signature && tos_agreed_at) ? (
              <div className="h-48 flex">
                {!isTermsAgreed ? (
                  <div className="mr-2">
                    <div className="py-3 px-1.5 border-b border-gray-150 cursor-pointer" onClick={undo}>
                      <ArrowUturnLeftIcon
                        className={classNames("w-6 h-6", undoDisabled ? "text-gray-150" : "text-black")}
                      />
                    </div>
                    <div className="py-3 px-1.5 border-b border-gray-150 cursor-pointer" onClick={redo}>
                      <ArrowUturnRightIcon
                        className={classNames(" w-6 h-6", redoDisabled ? "text-gray-150" : "text-black")}
                      />
                    </div>
                    <div className="py-3 px-1.5 border-b border-gray-150 cursor-pointer" onClick={clearCanvas}>
                      <TrashIcon className={classNames(" w-6 h-6", isCanvasEmpty ? "text-gray-150" : "text-black")} />
                    </div>
                  </div>
                ) : (
                  <></>
                )}
                {isRerendered ? (
                  <div className="w-full h-full rounded-md" style={{ background: "#FAFAFA" }} id="parentCanvas">
                    <SignatureCanvas
                      ref={setRef}
                      onEnd={handleDrawEnd}
                      canvasProps={{ className: "sigCanvas" }}
                      penColor="#000"
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            ) : <></>}
          </>
        ) : (
          <div className="max-w-96">
            <img src={tos_signature} alt="sign" />
          </div>
        )}
        <div className="mt-6 mb-2">
          {isTermsAgreed ? (
            <div className="flex flex-row h-full justify-between items-center">
              <div className="flex flex-col">
                <div className="text-baselg font-bold">Terms & Conditions Accepted</div>
                <div className="flex flex-row items-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 mr-1" />
                  <div className="text-md text-gray-450">{parseDateTime(tos_agreed_at)?.toFormat("MMM d yyyy, h:mm a")}</div>
                </div>
                <div className="text-green-500 cursor-pointer" onClick={handlePrint}>
                  Terms and Conditions.pdf
                </div>
              </div>
              <Button variant="white" size="large" onClick={onClose} className="w-44">
                Close
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-end h-full">
              <div className="text-gray-500 text-md mb-2">
                By clicking Accept & Sign I understand and agree that this is a legal representation of my signature
              </div>
              <div className="flex gap-2">
                {!!isAdminView && (
                  <Button variant="gray" size="large" onClick={() => handleAcceptAndSign()} disabled={!isCanvasEmpty}>
                    Mark as signed manually
                  </Button>
                )}
                <Button variant="green" size="large" type="submit" className="w-44" disabled={isCanvasEmpty}>
                  Accept & Sign
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </Modal>
  )
}

export default TermsAgreementModal
